import { Helmet } from 'react-helmet-async';

const DOMAIN = 'https://shangrilux.com';
const OG_IMG = 'https://images.unsplash.com/photo-1758931156945-68a907e06d64?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200&h=630&fit=crop';

const FAQS_IT = [
  { q: 'Posso personalizzare il nome della piattaforma?', a: 'Sì. Con i piani Professional ed Elite puoi configurare il nome della tua struttura in italiano e inglese. Con Elite ottieni il white-label completo: nessun riferimento a Shangrilux.' },
  { q: 'Le integrazioni smart home richiedono configurazione tecnica?', a: 'Dipende dal brand. Webhook e Home Assistant sono configurabili dall\'admin in pochi passi. Per KNX e BACnet potrebbe servire un installatore certificato.' },
  { q: 'Posso usare Shangrilux per più proprietà?', a: 'Sì. Dal piano Professional gestisci fino a 5 proprietà da un unico pannello admin. Con Elite le proprietà sono illimitate.' },
  { q: 'Come funziona la demo gratuita?', a: 'Compili il form, ricevi credenziali immediate. Hai 24 ore per esplorare un ambiente completo con dati di esempio. Poi si cancella automaticamente.' },
  { q: 'Come funziona la fatturazione?', a: 'I piani sono fatturati annualmente. Il prezzo mensile mostrato è il canone mensile equivalente — l\'importo viene addebitato una volta all\'anno.' },
  { q: 'Cosa succede se supero i limiti del mio piano?', a: 'Ti avvisiamo prima che tu raggiunga il limite. Puoi fare upgrade in qualsiasi momento mantenendo tutti i dati e le configurazioni.' },
  { q: 'Esiste un piano a vita (Lifetime)?', a: 'Sì, esiste una licenza Lifetime con accesso permanente e illimitato. È disponibile su richiesta con un\'offerta personalizzata. Contattaci per ricevere la tua proposta.' },
];
const FAQS_EN = [
  { q: 'Can I customize the platform name?', a: 'Yes. With Professional and Elite you can set your property name in Italian and English. Elite gives full white-label — no Shangrilux branding visible.' },
  { q: 'Do smart home integrations require technical setup?', a: 'It depends on the brand. Webhook and Home Assistant are configurable from the admin in a few steps. KNX and BACnet may require a certified installer.' },
  { q: 'Can I manage multiple properties?', a: 'Yes. From the Professional plan you manage up to 5 properties from one admin. Elite gives unlimited properties.' },
  { q: 'How does the free demo work?', a: 'Fill the form, get immediate credentials. You have 24 hours to explore a complete environment with sample data. Then it auto-deletes.' },
  { q: 'How does billing work?', a: 'Plans are billed annually. The monthly price shown is the monthly equivalent of the annual fee, not a separate charge.' },
  { q: 'What happens if I exceed my plan limits?', a: 'We notify you before you reach the limit. You can upgrade at any time keeping all your data and configurations.' },
  { q: 'Is the Lifetime plan available?', a: 'The Lifetime plan (full access for life, €4,990 one-time) is available by invitation only. Contact us for details.' },
];

const META = {
  it: {
    home: {
      title: 'Shangrilux — Piattaforma Smart Access & Booking per Hospitality Luxury',
      description: 'Shangrilux è la piattaforma B2B SaaS all-in-one per hotel e affitti brevi luxury. 18 brand smart home, chiavi virtuali senza app, portale prenotazioni, Guest App PWA. White-label, bilingue IT/EN.',
      keywords: 'smart access hospitality, booking management luxury, chiavi virtuali hotel, gestione affitti brevi, smart home hotel, piattaforma SaaS hospitality, software gestione prenotazioni, accesso senza chiave hotel',
    },
    pricing: {
      title: 'Prezzi Shangrilux — Piani Essenziale, Professional, Elite e Lifetime',
      description: 'Confronta i piani Shangrilux. Da €300/mese per strutture singole a soluzioni Elite illimitate. Demo gratuita 24h senza carta di credito. Piano Lifetime disponibile su richiesta a €4.990.',
      keywords: 'prezzi Shangrilux, piani hospitality SaaS, software gestione hotel prezzi, confronto piani, piano lifetime hospitality, costo gestione affitti brevi',
    },
  },
  en: {
    home: {
      title: 'Shangrilux — Smart Access & Booking Management Platform for Luxury Hospitality',
      description: 'Shangrilux is the all-in-one B2B SaaS platform for luxury hotels and short-term rentals. 18 smart home brands, app-free virtual keys, booking portal, Guest App PWA. White-label, bilingual IT/EN.',
      keywords: 'smart access hospitality, luxury hotel management software, virtual keys hotel, short term rental management, smart home hotel, hospitality SaaS platform, keyless access hotel',
    },
    pricing: {
      title: 'Shangrilux Pricing — Essential, Professional, Elite & Lifetime Plans',
      description: 'Compare Shangrilux plans. From €300/month for single properties to unlimited Elite for luxury venues. Free 24h demo, no credit card required. Lifetime plan available on request at €4,990.',
      keywords: 'Shangrilux pricing, hospitality SaaS plans, hotel management software pricing, lifetime plan hospitality, short term rental software cost',
    },
  },
};

export function SeoHead({ page = 'home', lang = 'it', theme = 'dark' }) {
  const m = META[lang]?.[page] || META.it.home;
  const isIt = lang === 'it';

  const canonicalHome = isIt ? `${DOMAIN}/` : `${DOMAIN}/?lang=en`;
  const canonicalPricing = isIt ? `${DOMAIN}/pricing` : `${DOMAIN}/pricing?lang=en`;
  const canonical = page === 'home' ? canonicalHome : canonicalPricing;

  const altHome = isIt ? `${DOMAIN}/?lang=en` : `${DOMAIN}/`;
  const altPricing = isIt ? `${DOMAIN}/pricing?lang=en` : `${DOMAIN}/pricing`;
  const altHref = page === 'home' ? altHome : altPricing;
  const xDefault = page === 'home' ? `${DOMAIN}/` : `${DOMAIN}/pricing`;

  const faqs = lang === 'it' ? FAQS_IT : FAQS_EN;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Shangrilux',
    url: DOMAIN,
    logo: `${DOMAIN}/logo192.png`,
    founder: { '@type': 'Person', name: 'Francesco Russo', url: 'https://francescorusso.work' },
    description: isIt
      ? 'Piattaforma B2B SaaS per la gestione di accessi smart, prenotazioni e ospiti per hospitality luxury.'
      : 'B2B SaaS platform for smart access management, bookings and guests for luxury hospitality.',
    contactPoint: { '@type': 'ContactPoint', email: 'info@francescorusso.work', contactType: 'customer support' },
    sameAs: ['https://francescorusso.work'],
  };

  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Shangrilux',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: DOMAIN,
    description: m.description,
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'EUR',
      lowPrice: '300',
      highPrice: '1500',
      offerCount: '4',
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Shangrilux',
    url: DOMAIN,
    inLanguage: [isIt ? 'it-IT' : 'en-US', isIt ? 'en-US' : 'it-IT'],
  };

  return (
    <Helmet>
      <html lang={lang} />
      <title>{m.title}</title>
      <meta name="description" content={m.description} />
      <meta name="keywords" content={m.keywords} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="author" content="Francesco Russo — francescorusso.work" />
      <meta name="theme-color" content={theme === 'dark' ? '#080808' : '#faf8f5'} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang={lang} href={canonical} />
      <link rel="alternate" hrefLang={lang === 'it' ? 'en' : 'it'} href={altHref} />
      <link rel="alternate" hrefLang="x-default" href={xDefault} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Shangrilux" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={m.title} />
      <meta property="og:description" content={m.description} />
      <meta property="og:image" content={OG_IMG} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Shangrilux — Luxury Hospitality Management Platform" />
      <meta property="og:locale" content={isIt ? 'it_IT' : 'en_US'} />
      <meta property="og:locale:alternate" content={isIt ? 'en_US' : 'it_IT'} />

      {/* Twitter / X Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={m.title} />
      <meta name="twitter:description" content={m.description} />
      <meta name="twitter:image" content={OG_IMG} />
      <meta name="twitter:image:alt" content="Shangrilux — Luxury Hospitality Management Platform" />

      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
      {page === 'home' && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </Helmet>
  );
}
